import type { ReactNode, KeyboardEvent, HTMLAttributes } from "react";
import { createContext, useContext, useState, useRef, useEffect } from "react";
import { pillVariants } from "@markeroo/theme";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";
import PiPencilEditBoxStroke from "@markeroo/icons/src/stroke/general/PiPencilEditBoxStroke";
import { ui } from "@/strings";

// Context to communicate pill animation state to child tooltips
const PillAnimatingContext = createContext(false);

/**
 * Hook to check if the parent pill is currently animating.
 * Useful for disabling tooltips during pill expand/collapse animations.
 */
export function usePillAnimating(): boolean {
  return useContext(PillAnimatingContext);
}

export type PillVariant =
  | "default"
  | "emphasis"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "processing";

interface PillProps extends Omit<HTMLAttributes<HTMLSpanElement>, "onClick"> {
  /** The content to display in the pill */
  children: ReactNode;
  /** The visual variant of the pill */
  variant?: PillVariant;
  /** Icon to display before the content */
  icon?: ReactNode;
  /** Edit handler - shows edit icon on hover (clicking pill body also triggers edit) */
  onEdit?: () => void;
  /** Delete handler - shows delete button on hover */
  onDelete?: () => void;
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
  /** Accessible label for the pill (used when interactive) */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show animation (e.g., for newly added items) */
  isNew?: boolean;
  /** Animation class name to use (default: pill-shimmer-container) */
  animationClassName?: string;
  /** Test ID for testing */
  testId?: string;
  /**
   * @deprecated Use onEdit instead. Click handler - makes the pill interactive.
   */
  onClick?: () => void;
  /**
   * @deprecated No longer used - actions always expand on hover.
   */
  showDeleteAlways?: boolean;
}

// Hover colors for each variant - using brightness filter for consistent darkening
const hoverColors: Record<PillVariant, string> = {
  default: "hover:brightness-95",
  emphasis: "hover:brightness-95",
  success: "hover:brightness-95",
  warning: "hover:brightness-95",
  error: "hover:brightness-95",
  info: "hover:brightness-95",
  processing: "hover:brightness-95",
};

/**
 * Pill component - Displays content in a pill/chip format
 *
 * Variants:
 * - default: Subtle style for standard criteria tags (fades into background)
 * - emphasis: Stands out more for custom criteria or important items
 * - Semantic variants (success, warning, error, info, processing): For status badges
 *
 * Action modes:
 * - No actions: Static pill, no hover effects
 * - Delete only (onDelete): Expands on hover to reveal X icon
 * - Edit + delete (onEdit + onDelete): Expands on hover to reveal edit and X icons;
 *   clicking pill body triggers edit
 *
 * Features:
 * - Smooth width expansion animation on hover to reveal action icons
 * - Optional icon support (left side)
 * - Animation for newly added items
 */
export function Pill({
  children,
  variant = "default",
  icon,
  onEdit,
  onDelete,
  isDeleting = false,
  ariaLabel,
  className = "",
  isNew = false,
  animationClassName,
  testId,
  // Deprecated props - still supported for backwards compatibility
  onClick,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- deprecated prop, kept for backwards compatibility
  showDeleteAlways,
  ...rest
}: PillProps) {
  // Support deprecated onClick - treat it as onEdit
  const effectiveOnEdit = onEdit ?? onClick;
  const hasActions = Boolean(effectiveOnEdit) || Boolean(onDelete);
  const isClickable = Boolean(effectiveOnEdit);

  // Track animation state - true during the 200ms transition when hover state changes
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (!hasActions) return;
    setIsAnimating(true);
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    // Animation duration is 200ms
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (!hasActions) return;
    setIsAnimating(true);
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, 200);
  };

  // Base pill classes from theme
  const baseClasses = pillVariants[variant];

  // Interactive styles - apply group class for hover actions
  const interactiveClasses = hasActions
    ? `cursor-pointer ${hoverColors[variant]} group`
    : "";

  // Gap for icon (content always has gap when icon present)
  const gapClass = icon ? "gap-1.5" : "";

  // Extra left padding when no icon (for better visual balance)
  const noIconPaddingClass = !icon ? "pl-3" : "";

  // Animation classes for new items
  const animationClasses = isNew ? (animationClassName ?? "pill-shimmer-container") : "";

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (effectiveOnEdit && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      effectiveOnEdit();
    }
  };

  const handlePillClick = () => {
    if (effectiveOnEdit) {
      effectiveOnEdit();
    }
  };

  const pillContent = (
    <PillAnimatingContext.Provider value={isAnimating}>
      <span
        {...rest}
        onClick={isClickable ? handlePillClick : undefined}
        onKeyDown={isClickable ? handleKeyDown : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? "button" : "status"}
        className={`${baseClasses} ${interactiveClasses} ${gapClass} ${noIconPaddingClass} ${className}`}
        aria-label={isClickable ? ariaLabel : undefined}
        data-testid={testId}
      >
        {/* Icon */}
        {icon && (
          <span aria-hidden="true" className="flex items-center">{icon}</span>
        )}

      {/* Content */}
      <span className="leading-5 tracking-tight">{children}</span>

      {/* Action buttons container - expands on hover */}
      {hasActions && (
        <span
          className="inline-flex items-center overflow-hidden transition-[max-width,opacity] duration-200 ease-out max-w-0 opacity-0 group-hover:max-w-[60px] group-hover:opacity-100"
          aria-hidden={!hasActions}
        >
          {/* Edit button - only shown when onEdit is provided */}
          {effectiveOnEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                effectiveOnEdit();
              }}
              className="text-text-theme-muted hover:text-text-theme-primary transition-colors p-0.5 ml-1.5 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-500 flex-shrink-0"
              aria-label={`${ui.actions.edit} ${typeof children === "string" ? children : ""}`}
            >
              <PiPencilEditBoxStroke size={16} />
            </button>
          )}

          {/* Delete button */}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isDeleting}
              className={`text-text-theme-muted hover:text-semantic-error transition-colors p-0.5 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-semantic-error disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ${effectiveOnEdit ? "" : "ml-1.5"}`}
              aria-label={`${ui.actions.delete} ${typeof children === "string" ? children : ""}`}
            >
              <PiMultipleCrossCancelDefaultStroke size={16} />
            </button>
          )}
        </span>
      )}
      </span>
    </PillAnimatingContext.Provider>
  );

  // Wrap with animation container if needed
  if (isNew || animationClasses) {
    return (
      <div className={animationClasses} data-tutorial-target={isNew ? "new-criteria-card" : undefined}>
        {pillContent}
      </div>
    );
  }

  return pillContent;
}
